// Shapes of the auditor tools API (backend routes/auditorTools.routes.ts,
// docs/AUDITOR-TOOLS.md): the evidence trail per control, audit-date snapshots
// and seeded samples.

export type EvidenceOrigin = "upload" | "connected" | "register";

export interface TrailDocumentVersion {
  id: string;
  version: number;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
  importedAt: string | null;
  sourceModifiedAt: string | null;
  taggedToControl: boolean;
}

export interface TrailDocument {
  documentGroupId: string;
  fileName: string;
  source: string;
  origin: EvidenceOrigin;
  sourceWebUrl: string | null;
  policyTemplateId: string | null;
  latestVersionId: string;
  versions: TrailDocumentVersion[];
}

export interface TrailPolicy {
  templateId: string;
  title: string;
  state: "adopted" | "adopted_superseded" | "draft_only" | "not_generated" | string;
  adoptedDocumentId: string | null;
  adoptedFileName: string | null;
  adoptedAt: string | null;
  generatedVersion: number | null;
}

export interface TrailVerdict {
  runId: string;
  completedAt: string | null;
  status: string;
  evidenceFound: string[];
  gaps: string[];
  recommendations: string[];
  policyConflict: string | null;
  playbookCriteria: string[];
  carriedFrom: string | null;
  citations: Array<{ text: string; documentGroupIds: string[] }>;
}

export interface SoaHistoryEntry {
  id: string;
  at: string;
  action: string;
  role: string;
  actor: string | null;
  detail: string | null;
  changes: Array<{ field: string; before: unknown; after: unknown }>;
}

export interface ControlTrail {
  control: { id: string; frameworkId: string; kind: string; name: string; description: string; requiredEvidence: string[] };
  asAt: { snapshotId: string; takenAt: string; label: string | null; stageDate: string | null } | null;
  soa: { status: string; applicable: boolean | null; justification: string | null; approvalStatus: string; updatedAt: string | null } | null;
  documents: TrailDocument[];
  policies: TrailPolicy[];
  verdict: TrailVerdict | null;
  history: SoaHistoryEntry[];
}

export interface AuditSnapshotSummary {
  id: string;
  trigger: "schedule" | "on_demand";
  scheduleId: string | null;
  auditType: string | null;
  stageDate: string | null;
  label: string | null;
  takenByName: string | null;
  takenAt: string;
  frameworkIds: string[];
  overallScore: number | null;
  documentCount: number | null;
  soaRowCount: number | null;
  digest: string;
}

export interface AuditSnapshotDetail extends AuditSnapshotSummary {
  intact: boolean;
  scores: Array<{ id: string; score: number | null; implemented: number; total: number }>;
  soa: Array<{ controlId: string; frameworkId: string; status: string; applicable: boolean | null }>;
  evidence: Array<{ id: string; documentGroupId: string; version: number; fileName: string; source: string; enteredAt: string; tags: Array<{ frameworkId: string; controlRef: string }> }>;
  policies: Array<{ templateId: string; title: string; state: string; controlIds: string[] }>;
  verdicts: { runId: string; completedAt: string | null; count: number } | null;
}

export type SampleItemResult = "pending" | "tested" | "exception";

export const SAMPLE_RESULT_LABELS: Record<SampleItemResult, string> = {
  pending: "Not yet tested",
  tested: "Tested, no exception",
  exception: "Exception",
};

export interface SampleSummary {
  size: number;
  pending: number;
  tested: number;
  exceptions: number;
  complete: boolean;
}

export interface AuditSampleItem {
  id: string;
  position: number;
  recordId: string;
  label: string;
  recordDate: string | null;
  result: SampleItemResult;
  note: string | null;
  testedAt: string | null;
  findingId: string | null;
}

export interface AuditSample {
  id: string;
  frameworkId: string;
  population: string;
  periodStart: string | null;
  periodEnd: string | null;
  seed: string;
  sampleSize: number;
  populationCount: number;
  populationDigest: string;
  purpose: string | null;
  createdByName: string | null;
  createdAt: string;
  summary: SampleSummary;
  items?: AuditSampleItem[];
}

export interface SampleListResponse {
  samples: AuditSample[];
  populations: Record<string, { label: string; datedBy: string }>;
  maxSampleSize: number;
}

export const ORIGIN_LABELS: Record<EvidenceOrigin, string> = {
  upload: "Uploaded",
  connected: "Connected storage",
  register: "Platform register",
};

export const POLICY_STATE_LABELS: Record<string, string> = {
  adopted: "Adopted",
  adopted_superseded: "Adopted, behind the latest draft",
  draft_only: "Draft only, not evidence",
  not_generated: "Not generated or adopted",
};

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  "SoA entry updated": "Updated",
  "SoA entry reset": "Reset",
  "SOA entry approved": "Approved by consultant",
  "SoA entries inferred from intake answers": "Inferred from intake answers",
};
